
import React, { useEffect, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { AuthCard } from "@/features/auth/components/AuthCard";
import { LoadingState } from "@/components/error-handling/LoadingState";
import { AuthRecovery } from "@/components/auth/AuthRecovery";
import { useAuthRecovery } from "@/hooks/useAuthRecovery";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";

const Auth = () => {
  const { isAuthenticated, isLoading } = useAuth();
  const { showRecovery, reason } = useAuthRecovery();
  const navigate = useNavigate();
  const location = useLocation();
  const [authMode, setAuthMode] = useState<"login" | "signup" | "forgotPassword">("login");
  const [authLoading, setAuthLoading] = useState(false);
  const [authError, setAuthError] = useState<string | null>(null);

  console.log('[Auth] State:', { isAuthenticated, isLoading });

  useEffect(() => {
    if (isAuthenticated && !isLoading) {
      const intendedPath = location.state?.from || "/dashboard";
      console.log(`[Auth] Redirecting authenticated user to: ${intendedPath}`);
      navigate(intendedPath, { replace: true });
    }
  }, [isAuthenticated, isLoading, navigate, location.state]);

  const handleSubmit = async (values: any) => {
    setAuthLoading(true);
    setAuthError(null);

    try {
      if (authMode === "login") {
        const { error } = await supabase.auth.signInWithPassword({
          email: values.email,
          password: values.password,
        });
        if (error) throw error;
      } else if (authMode === "signup") {
        const { error } = await supabase.auth.signUp({
          email: values.email,
          password: values.password,
          options: {
            data: {
              first_name: values.first_name,
              last_name: values.last_name,
            }
          }
        });
        if (error) throw error;
        toast.success("Check your email for verification link");
      }
    } catch (error: any) {
      console.error("Auth error:", error);
      setAuthError(error.message || "An error occurred");
    } finally {
      setAuthLoading(false);
    }
  };

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <LoadingState />
      </div>
    );
  }

  // Don't render auth form if user is authenticated (will redirect)
  if (isAuthenticated) {
    return null;
  }

  // Show recovery component if needed
  if (showRecovery) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background p-4">
        <AuthRecovery />
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-background p-4">
      <AuthCard 
        onSubmit={handleSubmit}
        isLoading={authLoading}
        authMode={authMode}
        setAuthMode={setAuthMode}
        authError={authError}
      />
    </div>
  );
};

export default Auth;
